import dayjs from 'dayjs';
import {
  add,
  chain,
  cloneDeep,
  compact,
  escapeRegExp,
  first,
  get,
  isEmpty,
  isString,
  keyBy,
  map,
  orderBy,
  range,
  sortBy,
  toLower,
  toString,
  toUpper,
  uniq,
} from 'lodash';
import setWith from 'lodash/fp/setWith';
import { Dispatch, SetStateAction } from 'react';
import type { Path } from 'react-hook-form';
import { normalizeText } from 'src/components/modal/productListModal/highlightKeyword/HighlightKeyword';
import { SortOptions } from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { ISO_DATE_FORMAT, US_DATE_FORMAT } from 'src/constants';
import {
  IExpiredStatus,
  IResponseType,
  IScreen,
  IScreenOrientation,
} from 'src/interfaces';

import { lang } from './language';

const ESCAPED_BACKSLASH_PATTERN = /(?<=\\)\\/;
const ESCAPED_REGEX_PATTERN = /(?<=\\)[.*+?^${}()|[\]]/;

export function isArrayOfStrings(obj: any) {
  return Array.isArray(obj) && obj.every((value) => typeof value === 'string');
}

export function getKeyPaths(
  obj: any,
  key: string,
  currentPath = '',
  result: string[] = [],
) {
  if (typeof obj !== 'object' || obj === null) {
    return result;
  }

  for (const property in obj) {
    if (obj.hasOwnProperty(property)) {
      const newPath =
        currentPath === '' ? property : `${currentPath}.${property}`;

      if (Array.isArray(obj[property])) {
        obj[property].forEach((item: any, index: any) => {
          getKeyPaths(item, key, `${newPath}[${index}]`, result);
        });
      } else {
        getKeyPaths(obj[property], key, newPath, result);
      }

      if (property === key) {
        result.push(newPath);
      }
    }
  }

  return result;
}

/**
 * Deep set an object value using a string path
 * @param obj The object to set the value on (will not be mutated)
 * @param path The string path to the object property to be set (e.g. 'a.b.c')
 * @param value The value to set
 * @returns The new object with the value set
 */
export function deepObjectSetByPath(obj: any, path: string, value: any) {
  return setWith(Object, path, value, obj);
}

export const onClickScrollToTop = (
  containerRef: React.RefObject<HTMLInputElement>,
) => {
  if (containerRef.current) {
    containerRef.current.scrollTo(0, 0);
  }
};

export const escapeSearchTerms = (
  keywords: string[] | string,
  storedKeywords: string[],
) => {
  const keywordsToEscape = Array.isArray(keywords) ? keywords : [keywords];

  const escaped = keywordsToEscape
    .filter(Boolean)
    .map((term) =>
      storedKeywords.includes(term)
        ? term
        : mapEscapeRegExWithExclusions(term, []),
    );

  return escaped;
};

const escapeRegExWithExclusions = (value: string, excludedChars: string[]) => {
  const escapedChars = [];
  for (let char of value) {
    if (excludedChars.includes(char)) {
      escapedChars.push(char);
    } else {
      escapedChars.push(escapeRegExp(char));
    }
  }

  return escapedChars.join('');
};

export const mapEscapeRegExWithExclusions = (
  data: string | string[],
  excludedChars: string[],
) => {
  const escapedString = Array.isArray(data)
    ? data.map((str) => escapeRegExWithExclusions(str, excludedChars))
    : escapeRegExWithExclusions(data, excludedChars);

  return escapedString;
};

export const escapeRegexAndEncodeUriParam = (param: string) => {
  return encodeURIComponent(escapeRegExp(param));
};

export const formatQuery = (query: string) => {
  if (!query) return [];

  const queries = query
    .replace('?', '')
    .split('&')
    .map((item) => {
      const [key, value] = item.split('=');

      let escapedString = escapeRegExp(value);

      // Replace the escaped asterisk as supported by the API
      escapedString = escapedString
        .replace(/\\\*/g, '*')
        .replace(/\\\+/g, '+')
        .replace(/\\\./g, '.');

      return `${key}=${escapedString}`;
    });

  return queries;
};

export const checkMultipleExist = (
  resourceArray: unknown[],
  targetArray: unknown[],
) => {
  if (isEmpty(targetArray)) return false;

  return targetArray.some((value) => {
    return resourceArray.includes(value);
  });
};

export const checkByKeywords = (keywords: string[], item: unknown) => {
  for (const key of keywords) {
    const normalizedKey = normalizeText(key);
    const unescapedKey = undoEscapedRegExp(normalizedKey);

    if (
      !isEmpty(item) &&
      toUpper(normalizeText(toString(item))).includes(toUpper(unescapedKey))
    ) {
      return true;
    }
  }

  return false;
};

export const truncateWithEllipsis = (text: string, maxLength: number) => {
  if (text.length > maxLength) {
    return text.slice(0, maxLength - 3) + '...';
  }

  return text;
};

export const generateEnumOptions = <T = unknown>(
  enumValue: T,
  labels: string[] = [],
) => {
  return chain(enumValue)
    .entries()
    .map(([label, value], index) => ({
      label: labels[index] ?? label,
      value: value,
    }))
    .sortBy('label')
    .value();
};

export const generateOptions = <T = unknown>(
  data: T[],
  labelPath: string,
  valuePath?: string,
  sortBy?: string,
  order: 'asc' | 'desc' = 'asc',
  maxLabelLength: number = 30,
  disableSort: boolean = false,
) => {
  return chain(data)
    .uniqBy(labelPath)
    .filter((item) => !isEmpty(get(item, labelPath, '')))
    .thru((items) =>
      disableSort
        ? items
        : orderBy(
            items,
            [(item) => toLower(get(item, sortBy ?? labelPath))],
            order,
          ),
    )
    .map((item) => ({
      label: truncateWithEllipsis(
        get(item, labelPath, '') as string,
        maxLabelLength,
      ),
      value: get(item, valuePath ?? labelPath, ''),
    }))
    .value();
};

// Check presence of elements of one array in another based off a common field.

export const getCompareAndFilteredData = <T>(
  referenceItems: unknown[],
  lookupCollection: T[],
  field: string,
): T | undefined => {
  if (!referenceItems || !lookupCollection) {
    return undefined;
  }

  const categoryIdMap = keyBy(lookupCollection, 'id');

  const filteredItems = map(referenceItems, (item) => {
    const categoryId = get(item, field);

    return categoryIdMap[categoryId];
  });

  return first(filteredItems);
};

export const generateSortOption = (selectedOption: {
  option: SortOptions;
  order: 'desc' | 'asc';
}): string => {
  const option = `${selectedOption.order === 'desc' ? '-' : ''}${
    selectedOption.option === SortOptions.ALPHABETICALLY
      ? `*${selectedOption.option}`
      : selectedOption.option
  }`;

  return option;
};

export const moveArrayItem = <T = unknown>(
  source: T[],
  oldIndex: number,
  newIndex: number,
) => {
  const newArray = cloneDeep(source);

  const movedItem = newArray.splice(oldIndex, 1)[0];
  newArray.splice(newIndex, 0, movedItem);

  return newArray;
};

export const generateQuery = (include: string[], extraQuery: string[] = []) => {
  const includeQuery = !isEmpty(include) ? `include=${include.join(',')}` : '';

  const formattedQuery = extraQuery.map((item) => item.replace('?', ''));

  const query = compact([includeQuery, ...formattedQuery]);

  return !isEmpty(query)
    ? `?${new URLSearchParams(query.join('&')).toString()}`
    : ''; //To convert special characters into query params characters, add the URLSearchParams object.
};

export const getScale = (
  windowWidth: number,
  windowHeight: number,
  layoutScreens: IScreen[],
) => {
  if (windowWidth !== 0 && windowHeight !== 0 && layoutScreens.length > 0) {
    const layoutWidth =
      layoutScreens.reduce((acc, item) => {
        return add(acc, item.width ?? 0);
      }, 0) ?? 0;

    const minScale = Math.min(
      windowWidth / layoutWidth,
      windowHeight / layoutScreens[0]?.height,
    );

    return Math.floor(minScale * 0.7 * 1000) / 1000;
  } else {
    // Return a default scale or handle the case when windowWidth is 0
    return 0.1;
  }
};

const isVeriticalScreen = (screen: { height: number; width: number }) => {
  return screen?.height > screen?.width;
};

export const getResponsiveScale = (
  windowWidth: number,
  layoutScreens: IScreen[],
  responseType: IResponseType,
) => {
  if (windowWidth === 0) return 0;

  const layoutWidth =
    layoutScreens.reduce((acc, item) => add(acc, item.width ?? 0), 0) ?? 0;

  const screenCount = layoutScreens.length;
  const orientation = isVeriticalScreen(layoutScreens[0])
    ? IScreenOrientation.VERTICAL
    : IScreenOrientation.HORIZONTAL;

  const scales = {
    [IResponseType.CARD]: {
      [IScreenOrientation.VERTICAL]:
        screenCount === 1 ? 0.5 : screenCount < 5 ? 0.9 : 0.8,
      [IScreenOrientation.HORIZONTAL]:
        screenCount === 1 ? 0.8 : screenCount < 5 ? 0.9 : 0.8,
    },
    [IResponseType.MODAL]: {
      [IScreenOrientation.VERTICAL]:
        screenCount === 1
          ? 0.12
          : screenCount === 2
          ? 0.2
          : screenCount < 4
          ? 0.3
          : 0.5,
      [IScreenOrientation.HORIZONTAL]:
        screenCount === 1
          ? 0.3
          : screenCount === 2
          ? 0.3
          : screenCount < 5
          ? 0.5
          : 0.9,
    },
    [IResponseType.PAGE]: {
      [IScreenOrientation.VERTICAL]:
        screenCount === 1 ? 0.28 : screenCount === 2 ? 0.5 : 0.7,
      [IScreenOrientation.HORIZONTAL]: screenCount < 5 ? 0.6 : 0.9,
    },
  };

  const internalScale = scales[responseType]?.[orientation] ?? 0.7;

  return Math.floor((windowWidth / layoutWidth) * internalScale * 1000) / 1000;
};

export const getPlainText = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  if (doc.body.textContent === html) return html;
  const texts = Array.from(doc.body.childNodes).map(
    (node) => `${node.textContent} `,
  );

  return texts.join('').trim();
};

export const customSort = (a: string | number, b: string | number) => {
  // Convert values to strings for comparison
  const stringA = String(a).toLowerCase();
  const stringB = String(b).toLowerCase();

  // Handle numerical values
  const numRegex = /^\d+$/;
  if (numRegex.test(stringA) && numRegex.test(stringB)) {
    return Number(stringA) - Number(stringB);
  }

  // Handle alphanumeric values
  return stringA.localeCompare(stringB, undefined, { numeric: true });
};

export const naturalSort = <T = unknown>(
  array: T[],
  sort?: Path<T>,
  order: 'desc' | 'asc' = 'asc',
) => {
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
  });

  return [...array].sort((a, b) => {
    const aValue = isString(a) ? a : toString(get(a, sort ?? ''));
    const bValue = isString(b) ? b : toString(get(b, sort ?? ''));

    if (order === 'asc') {
      return collator.compare(aValue, bValue);
    } else {
      return collator.compare(bValue, aValue);
    }
  });
};

export const copyToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {}
};

export const getExpiryStatusMessage = (endDate: string | null | undefined) => {
  const parsedEndDate = dayjs(endDate, 'YYYY-MM-DD');
  const today = dayjs().startOf('day');
  const isExpired = dayjs().isAfter(dayjs(endDate));
  const differenceInDays = parsedEndDate.diff(today, 'day');

  if (isExpired) {
    return IExpiredStatus.expired;
  } else if (differenceInDays <= 60) {
    return `${lang.expiringIn} ${differenceInDays}d`;
  }

  return null;
};

export const getDaysFromExpiryMessage = (
  message: string | null,
): number | null => {
  if (!message) return null;
  const daysMatch = message.match(/(\d+)d/);

  return daysMatch ? parseInt(daysMatch[1], 10) : null;
};

export function updateOrInsertItem<T extends object>(
  array: T[],
  item: T,
  key: string,
  isMerge?: boolean,
): T[] {
  const idx = array.findIndex((i) => get(i, key) === get(item, key));
  if (idx !== -1) {
    const updated = isMerge ? { ...array[idx], ...item } : item;

    return [...array.slice(0, idx), updated, ...array.slice(idx + 1)];
  } else {
    return [...array, item];
  }
}

export const getPixelRange = (
  min: number,
  max: number,
  step: number = 2,
  extra?: string,
): string[] => {
  const pixelRange = sortBy([
    ...range(min, max + 1, step).map((item) => item + 'px'),
    ...(extra ? [extra] : []),
  ]);

  return uniq(pixelRange);
};
export const formatDate = (date: string) => {
  if (!date) {
    return ''; // Return blank if the date is null or undefined
  }

  return dayjs(date).format(US_DATE_FORMAT);
};

export const formatIsoDateOrNull = (date?: string | null) =>
  date ? dayjs(date).format(ISO_DATE_FORMAT) : null;

export const isEscapedRegExp = (str: string) => {
  const escapedPattern = /\\[.*+?^${}()|[\]\\]/g;

  return escapedPattern.test(str);
};

const toggleAccordion = (accordions: number[], groupId: number): number[] => {
  const isAccordionOpen = accordions.includes(groupId);
  if (isAccordionOpen) {
    return accordions.filter((id: number) => id !== groupId);
  }

  return [...accordions, groupId];
};

export const handleAccordionToggle = (
  groupId: number,
  openAccordions: number[],
  setOpenAccordions: Dispatch<SetStateAction<number[]>>,
) => {
  const newAccordions = toggleAccordion(openAccordions, groupId);
  setOpenAccordions(newAccordions);
};

export const getColor = (type: string) => {
  switch (type) {
    case 'location':
      return 'primary';
    case 'location_owner':
      return 'success';
    default:
      return 'default';
  }
};

export const trimmedLowercaseString = (str: string) =>
  str.replace(/\s+/g, '').toLowerCase();

export const getFileExtension = (mimeTypes: string = '') => {
  const mimeToExt: Record<string, string> = {
    'image/png': '.png',
    'image/jpeg': '.jpeg',
    'image/jpg': '.jpg',
    'image/gif': '.gif',
    'video/webm': '.webm',
  };

  const extensions = mimeTypes
    .split(',')
    .map((mimeType) => mimeToExt[mimeType.trim()] || mimeType.trim());

  return extensions.length > 1
    ? `${extensions.slice(0, -1).join(', ')} or ${extensions.slice(-1)}`
    : extensions[0] || '';
};

export const calculateOffset = (base: number, adjustment: number) =>
  Math.round(base + adjustment);

export function hasPaginatedData<T>(paginatedData?: {
  pages: { data: T[] }[];
}): boolean {
  return (
    !!paginatedData &&
    paginatedData.pages.flatMap((page) => page.data).length > 0
  );
}

const undoEscapedBackslash = (escaped_str: string) => {
  if (ESCAPED_BACKSLASH_PATTERN.test(escaped_str)) {
    const unescaped_array = [];
    let last_charac = null;
    for (const charac of chain(escaped_str).value()) {
      if (charac === '\\') {
        if (last_charac === null) {
          last_charac = charac;
          unescaped_array.push(charac);
        } else last_charac = null;
      } else unescaped_array.push(charac);
    }

    return unescaped_array.join('');
  }

  return escaped_str;
};

export const undoEscapedRegExp = (escaped_str: string) => {
  if (ESCAPED_REGEX_PATTERN.test(escaped_str)) {
    const unescaped_str = escaped_str.replaceAll(/(?<!\\)\\/g, '');

    return undoEscapedBackslash(unescaped_str);
  }

  return undoEscapedBackslash(escaped_str);
};

export const normalizeSearchKeywordsBackslashes = (
  keywords: string | string[],
): string | string[] => {
  if (Array.isArray(keywords))
    return keywords.map((keyword) => keyword.replaceAll(/\\/g, '\\\\'));
  else return keywords.replaceAll(/\\/g, '\\\\');
};

export const groupByToMap = <T>(
  arr: T[],
  func: (item: T) => string | number,
): Map<string, T[]> => {
  return arr.reduce((acc, o) => {
    const key = func(o);

    if (!acc.has(key)) acc.set(key, []);

    acc.get(key).push(o);

    return acc;
  }, new Map());
};
